import { useLayoutEffect, useState } from 'react'

import { thumbnailLargeNoFallback, thumbnailMediumNoFallback } from '@utils/thumbnails'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { size } from '@styles/theme'
import styled from '@emotion/styled'

import { PiCaretLeftBold, PiGearSixBold } from 'react-icons/pi'
import { ZoomableImage } from '@elements/ZoomableImage'
import ModalContainer from '@elements/ModalContainer'
import FloatingButton from '@elements/FloatingButton'
import SwipeableViews from 'react-swipeable-views'

const BackButton = styled(FloatingButton)`
  position: absolute;
  top: ${size(2)};
  left: ${size(2)};

  padding: ${size(1)};
  height: auto;
  min-width: 0;
`

const SettingsButton = styled(FloatingButton)`
  position: absolute;
  top: ${size(2)};
  right: ${size(2)};

  padding: ${size(1)};
  height: auto;
  min-width: 0;
`

const NumberDisplay = styled.div`
  position: absolute;
  top: ${size(3)};
  left: 50%;
  transform: translateX(-50%);
`

const IndexBubbles = styled.div`
  display: flex;
  align-items: center;
  padding: ${size(1)};
  height: ${size(6)};

  position: absolute;
  bottom: ${size(2)};
  left: 50%;
  transform: translateX(-50%);

  border-radius: 100px;
  background-color: black;
`

const Bubble = styled.img`
  height: ${size(3)};
  width: ${size(3)};
  border-radius: 100px;
  transition-duration: 200ms;
`

const NearbyImageViewer = ({ open, user, openNearbySettings, closeNearbySettings, isCurrentUser, closeImageViewer }) => {
  const navigate = useNavigate()
  const [selectedItem, setSelectedItem] = useState(null)
  const [searchParams] = useSearchParams()
  const [images, setImages] = useState([])
  const hasThumbnails = user?.nearbyMedia?.thumbnails?.length > 0
  // WARNING: This probably won't change correctly when the user nearbyMedia ges updated, but the user is in the useEffect, so maybe changes to nearbyMedia will be recognized

  useLayoutEffect(() => {
    if (open && user && hasThumbnails) {
      const imageArr = [
        { large: thumbnailLargeNoFallback(user?.nearbyMedia?.thumbnails?.[0]), medium: thumbnailMediumNoFallback(user?.nearbyMedia?.thumbnails?.[0]) },
        { large: thumbnailLargeNoFallback(user?.nearbyMedia?.thumbnails?.[1]), medium: thumbnailMediumNoFallback(user?.nearbyMedia?.thumbnails?.[1]) },
        { large: thumbnailLargeNoFallback(user?.nearbyMedia?.thumbnails?.[2]), medium: thumbnailMediumNoFallback(user?.nearbyMedia?.thumbnails?.[2]) },
        { large: thumbnailLargeNoFallback(user?.nearbyMedia?.thumbnails?.[3]), medium: thumbnailMediumNoFallback(user?.nearbyMedia?.thumbnails?.[3]) },
      ].filter((img) => !!img.large || !!img.medium)
      
      // Close the image viewer if there are no images
      if (imageArr.length === 0) {
        closeNearbySettings()
        closeImageViewer()
      }
      
      // Set the current image that we are viewing
      let selectedIndex;
      if (!selectedItem) {
        selectedIndex = parseInt(searchParams.get('thumbnailIndex') || 0)
      } else {
        selectedIndex = selectedItem
      }

      // Makes sure the if -1 is returned from findIndex, or if the selectedIndex doesn't exist, we fall back to 0
      if (!selectedIndex || selectedIndex < 0) selectedIndex = 0
      // Makes sure that if the user deletes the last image while also viewing the same image, we send the user to the next existing image
      else if (selectedIndex >= imageArr.length) selectedIndex = imageArr.length - 1

      // Set new state
      setSelectedItem(selectedIndex)
      setImages(imageArr)
    } else if (!open) {
      setTimeout(() => {
        setSelectedItem(null)
      }, 250)
    }
  }, [open, user, hasThumbnails])

  const renderImages = () => {
    return images.map(({ large }, index) => (
      <ZoomableImage
        key={index}
        src={large}
      />
    ))
  }

  const renderCurrentIndex = () => {
    if (!images || images.length === 0 || !hasThumbnails) return;
    const bubbles = []
    for (let i = 0; i < images.length; i++) {
      bubbles.push(
        <Bubble
          key={i}
          onClick={() => setSelectedItem(i)}
          style={{
            height: i === selectedItem ? size(4.5) : size(3),
            width: i === selectedItem ? size(4.5) : size(3),
            marginRight: i === images.length - 1 ? '0' : size(0.5),
          }}
          src={images[i]?.medium}
        />
      )
    }
    return bubbles
  }

  return (
    <ModalContainer
      noHeader
      open={open}>
      <SwipeableViews
        onChangeIndex={(i) => setSelectedItem(i)}
        index={selectedItem || 0}
        enableMouseEvents
        axis='x'>
        {renderImages()}
      </SwipeableViews>
      <BackButton
        onClick={() => navigate(-1)}>
        <PiCaretLeftBold size={size(2.5)} />
      </BackButton>
      {isCurrentUser &&
        <SettingsButton
          onClick={openNearbySettings}>
          <PiGearSixBold size={size(2.5)} />
        </SettingsButton>
      }
      <NumberDisplay>
        {selectedItem + 1} of {images.length}
      </NumberDisplay>
      <IndexBubbles>
        {renderCurrentIndex()}
      </IndexBubbles>
    </ModalContainer>
  )
}

export default NearbyImageViewer
